import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { Config } from '../config';
import Sidebar from '../partials/Sidebar';
import Header from '../partials/Header';
import OpenItems from '../partials/dashboard/OpenItems';
import CVECard from '../partials/dashboard/CVECard';

/**
 * The landing page: exploitation status, and where it touches our own work.
 *
 * Not a news page. Nothing here is a headline, an opinion or a story — every
 * row is a dated fact from CISA's Known Exploited Vulnerabilities catalogue,
 * which is the authoritative statement that a vulnerability is being used
 * against real targets rather than merely scoring highly.
 *
 * The page earns its place through the intersection: Kingpin knows which CVEs
 * we have found, so a KEV entry can be shown as *ours*. Those come first and
 * are visually separated from the rest of the catalogue, because one is work
 * and the other is context. If the context ever crowds out the work, this has
 * become a feed and should be cut back.
 */

function Badge({ children, tone = 'slate' }) {
  const tones = {
    red: 'bg-red-500/15 text-red-400 border-red-500/30',
    amber: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    slate: 'bg-slate-700/40 text-slate-400 border-slate-600/40',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded border text-[10px] font-medium whitespace-nowrap ${tones[tone]}`}>
      {children}
    </span>
  );
}

function KevRow({ entry, ours }) {
  const found = (entry.scan_findings || 0) + (entry.engagement_findings || 0);
  return (
    <div className={`py-3 ${ours ? 'px-3 -mx-3 rounded-lg bg-slate-800/30' : ''}`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-sm font-medium text-slate-100 tabular-nums">{entry.cve}</span>
        <span className="text-xs text-slate-500">
          {entry.vendor}{entry.product ? ` · ${entry.product}` : ''}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {entry.ransomware && <Badge tone="red">Ransomware</Badge>}
          {ours && entry.past_due && <Badge tone="amber">Past CISA due date</Badge>}
          {ours && (
            <Badge tone="amber">
              {found} {found === 1 ? 'finding' : 'findings'}
            </Badge>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-300 mt-1">{entry.name}</p>

      {ours ? (
        <p className="text-[11px] text-slate-500 mt-1.5">
          <span className="text-slate-400">Required action:</span> {entry.action}
        </p>
      ) : (
        <p className="text-[11px] text-slate-600 mt-1">Added {entry.date_added}</p>
      )}

      {ours && (
        <p className="text-[11px] text-slate-600 mt-1">
          Added to KEV {entry.date_added}
          {entry.scan_findings > 0 && ` · ${entry.scan_findings} in scans`}
          {entry.engagement_findings > 0 && ` · ${entry.engagement_findings} in engagements`}
        </p>
      )}
    </div>
  );
}

function Landing() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [kev, setKev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${Config.APIBaseURL}/threat/kev`, {
      headers: { access_token: `Bearer ${token}` },
      withCredentials: true,
    })
      .then(({ data }) => { setKev(data); setError(null); })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, []);

  const matched = kev?.matched || [];
  const recent = kev?.recent || [];
  const shown = showAll ? recent : recent.slice(0, 8);

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-[90rem] mx-auto">

            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-100">Radar</h1>
              <p className="text-sm text-slate-500 mt-1">
                Exploitation status · CISA Known Exploited Vulnerabilities
                {kev?.catalog?.version && ` ${kev.catalog.version}`}
                {kev?.catalog?.count && ` · ${kev.catalog.count} entries`}
              </p>
            </div>

            {error && (
              <div className="mb-6 p-3 rounded-lg bg-red-950/30 border border-red-500/40 text-xs text-slate-300">
                {error}
              </div>
            )}

            {/* A stale catalogue is still worth showing; an empty one would
                imply nothing is being exploited, which is never true. */}
            {kev?.error && (
              <div className="mb-6 p-3 rounded-lg bg-amber-950/20 border border-amber-500/30 text-xs text-amber-200/80">
                Could not refresh the catalogue ({kev.error}). Showing the last
                copy retrieved{kev.fetched_at ? ` at ${kev.fetched_at.slice(0, 16).replace('T', ' ')}` : ''}.
              </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 space-y-6">

                {/* Ours first. This is the only part of the page that is work. */}
                <div className="rounded-lg bg-slate-900/50 border border-slate-800 p-4">
                  <div className="flex items-baseline justify-between mb-1">
                    <h2 className="text-sm font-semibold text-slate-200">
                      Exploited, and found in our data
                    </h2>
                    <span className="text-[11px] text-slate-500">
                      {matched.length} of {kev?.catalog?.count || '—'}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mb-2">
                    CVEs recorded in an open scan finding or an engagement finding that
                    CISA lists as exploited in the wild.
                  </p>

                  {loading ? (
                    <div className="py-10 flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
                    </div>
                  ) : matched.length === 0 ? (
                    <p className="text-xs text-slate-600 py-6">
                      None of the CVEs we have recorded appear in the catalogue.
                      That is a real result, not an empty widget — it means nothing
                      we have found is known to be exploited in the wild.
                    </p>
                  ) : (
                    <div className="divide-y divide-slate-800">
                      {matched.map((entry) => (
                        <KevRow key={entry.cve} entry={entry} ours />
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-lg bg-slate-900/50 border border-slate-800 p-4">
                  <div className="flex items-baseline justify-between mb-1">
                    <h2 className="text-sm font-semibold text-slate-200">
                      Added in the last {kev?.recent_days || 30} days
                    </h2>
                    <span className="text-[11px] text-slate-500">{recent.length}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mb-2">
                    Newly confirmed as exploited. None of these match anything we
                    have recorded — worth knowing what to look for.
                  </p>

                  {loading ? null : recent.length === 0 ? (
                    <p className="text-xs text-slate-600 py-6">
                      Nothing added in this period.
                    </p>
                  ) : (
                    <>
                      <div className="divide-y divide-slate-800">
                        {shown.map((entry) => (
                          <KevRow key={entry.cve} entry={entry} />
                        ))}
                      </div>
                      {recent.length > shown.length && (
                        <button
                          onClick={() => setShowAll(true)}
                          className="mt-3 text-[11px] text-indigo-400 hover:text-indigo-300"
                        >
                          Show the remaining {recent.length - shown.length}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* The rail: platform state and the raw CVE stream. Both are
                  reference material, which is why neither leads the page. */}
              <div className="space-y-6">
                <OpenItems onOpen={navigate} />
                <CVECard className="bg-slate-900/50 border border-slate-800 rounded-lg h-[420px] overflow-auto" />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Landing;
